import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { queryClient } from 'utils/queryClient'

import useDeviceRPC, {
  Device,
  DeviceRequestType,
  shouldRefetch,
} from './useDeviceRPC'

export type Params = {
  /**
   * The ID of the app to fetch the device from
   */
  appId: string
  /**
   * The peer key of the device to fetch
   */
  peerKey: string
  /**
   * The cloud domain URL for the app
   */
  cloudUrl?: string
}

type Return = {
  /**
   * Whether the device is currently being fetched.
   */
  isLoading: boolean
  /**
   * The device data
   */
  device: Device | null
  /**
   * An error message if an error occurred while fetching the device
   */
  errorMessage?: string
  /**
   * A callback to fetch a bundle of attachments for the device
   */
  fetchBundle: (attachmentIds: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to fetch the logs for the device, provided a specific attachment ID
   */
  fetchLogs: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to fetch the flame graph for the device, provided a specific attachment ID
   */
  fetchFlamegraph: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to fetch metrics for the device, provided a specific attachment ID
   */
  fetchMetrics: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to request a bundle for the device
   */
  requestBundle: () => Promise<void>
  /**
   * A callback to request logs for the device
   */
  requestLogs: () => Promise<void>
  /**
   * A callback to request flame graph for the device
   */
  requestFlamegraph: () => Promise<void>
  /**
   * A callback to request metrics for the device
   */
  requestMetrics: () => Promise<void>
  /**
   * A callback to cancel a pending data request for the device
   */
  cancelDataRequest: (requestType: DeviceRequestType) => Promise<void>
  /**
   * A callback to refetch the device
   */
  refetch: () => Promise<void>
}

const BASE_QUERY_KEY = 'getDeviceByPeerKey'

/**
 * A hook that provides device data and log requests operations for a specific device.
 */
export default function useDeviceByPeerKey({
  appId,
  cloudUrl,
  peerKey,
}: Params): Return {
  const {
    accessToken,
    getDeviceByPeerKey,
    requestLogs,
    requestFlamegraph,
    requestBundle,
    requestMetrics,
    fetchLogs,
    fetchFlamegraph,
    fetchBundle,
    fetchMetrics,
    cancelDataRequest,
    refreshToken,
  } = useDeviceRPC({ appId, cloudUrl })

  const {
    data: device,
    isLoading,
    error,
    refetch,
  } = useQuery(
    [BASE_QUERY_KEY, appId, peerKey],
    () => getDeviceByPeerKey(peerKey),
    {
      enabled: !!accessToken && !!peerKey,
      refetchInterval: shouldRefetch,
    },
  )
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [didRetry, setDidRetry] = useState(false)

  /**
   * A callback to handle a single retry when a 401 error is encountered. This will
   * refresh the RPC access token and refetch the device.
   */
  const handleTryAgain = useCallback(async () => {
    setDidRetry(true)
    await refreshToken()
    await refetch()
  }, [refreshToken, refetch])

  /**
   * An effect to properly handle errors when fetching the device. This will
   * primarily serve to set the error message, with the exception of 401 errors.
   */
  useEffect(() => {
    if (!error) return

    const fallback = 'An error occurred while fetching the device'
    if (isAxiosError(error)) {
      if (error.response?.status === 401 && !didRetry) {
        handleTryAgain()
      } else {
        setErrorMessage(error.response?.data?.message || fallback)
      }
    } else if (error instanceof Error) {
      setErrorMessage(error.message || fallback)
    } else {
      setErrorMessage(fallback)
    }
  }, [error, handleTryAgain, didRetry])

  const handleRequestLogs = useCallback(async () => {
    if (device) {
      await requestLogs(peerKey)
      await refetch()
    }
  }, [peerKey, requestLogs, device, refetch])

  const handleRequestFlamegraph = useCallback(async () => {
    if (device) {
      await requestFlamegraph(peerKey)
      await refetch()
    }
  }, [peerKey, requestFlamegraph, device, refetch])

  const handleRequestBundle = useCallback(async () => {
    if (device) {
      await requestBundle(peerKey)
      await refetch()
    }
  }, [peerKey, requestBundle, device, refetch])

  const handleRequestMetrics = useCallback(async () => {
    if (device) {
      await requestMetrics(peerKey)
      await refetch()
    }
  }, [peerKey, requestMetrics, device, refetch])

  const handleRefetch = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleCancelDataRequest = useCallback(
    async (requestType: DeviceRequestType) => {
      if (device) {
        await cancelDataRequest(peerKey, requestType)
        await refetch()
      }
    },
    [peerKey, cancelDataRequest, device, refetch],
  )

  return {
    isLoading: isLoading,
    device: device ?? null,
    fetchBundle,
    fetchLogs,
    fetchFlamegraph,
    fetchMetrics,
    requestBundle: handleRequestBundle,
    requestLogs: handleRequestLogs,
    requestFlamegraph: handleRequestFlamegraph,
    requestMetrics: handleRequestMetrics,
    cancelDataRequest: handleCancelDataRequest,
    errorMessage,
    refetch: handleRefetch,
  }
}

/**
 * A helper function to insert a device into the cache. This will be useful when
 * viewing the DevicesTable and then navigating to the DeviceDetailsScene.
 *
 * Note: A background fetch will still be triggered upon loading the
 * DeviceDetailsScene, but this will prevent the skeleton from being shown.
 */
export const cacheDeviceByPeerKey = async (
  device: Device,
  { appId, peerKey }: Omit<Params, 'cloudUrl'>,
) => queryClient.setQueryData([BASE_QUERY_KEY, appId, peerKey], device)
